import { useCallback, useRef, useState } from 'react';
import { useImageUpload } from './useImageUpload';
import { TEMP_PHOTO_ID_PREFIX } from '../components/reviews/reviewPhotoIds';
import type { ReviewPhoto } from '../components/reviews/ReviewPhotoUpload';

export interface ReviewPhotoUploadState {
  status: 'uploading' | 'uploaded' | 'error';
  progress: number;
  error?: string;
}

/**
 * Keyed by photo ID. Entries only exist for photos that have started uploading,
 * so lookups are explicitly allowed to miss.
 */
export type ReviewPhotoUploadStates = Record<string, ReviewPhotoUploadState | undefined>;

/**
 * Uploads the locally staged photos on a review form and returns the media IDs
 * to attach to the review.
 *
 * Every review type stages photos as blob-backed `temp-` entries and only
 * uploads them at submit time, so this hook is the single place that turns
 * those into real media records. It reports per-photo progress so the form can
 * show which upload is in flight, and it remembers photos that already
 * succeeded — a retry after a mid-batch failure re-sends only what is missing
 * rather than duplicating what already landed in Cloudinary.
 */
export const useReviewPhotoUpload = (entityId: string) => {
  const { uploadImage } = useImageUpload();
  const [uploadStates, setUploadStates] = useState<ReviewPhotoUploadStates>({});
  const [isUploading, setIsUploading] = useState(false);
  // Survives re-renders so a retry can skip photos that already uploaded.
  const uploadedMediaIds = useRef<Map<string, string>>(new Map());

  const setPhotoState = useCallback((photoId: string, state: ReviewPhotoUploadState) => {
    setUploadStates((prev) => ({ ...prev, [photoId]: state }));
  }, []);

  const reset = useCallback(() => {
    uploadedMediaIds.current.clear();
    setUploadStates({});
    setIsUploading(false);
  }, []);

  /**
   * Resolves to the media IDs for `photos`, uploading any that are still local.
   * Rejects on the first failed upload so the caller does not create a review
   * that silently drops photos.
   */
  const uploadPhotos = useCallback(
    async (photos: ReviewPhoto[]): Promise<string[]> => {
      const mediaIds: string[] = [];
      const pending = photos.filter(
        (photo) =>
          photo.id.startsWith(TEMP_PHOTO_ID_PREFIX) &&
          photo.file &&
          !uploadedMediaIds.current.has(photo.id)
      );

      if (pending.length > 0) {
        setIsUploading(true);
      }

      try {
        for (const photo of photos) {
          if (!photo.id.startsWith(TEMP_PHOTO_ID_PREFIX)) {
            // Already a persisted media ID (e.g. picked from an existing library).
            mediaIds.push(photo.id);
            continue;
          }

          const alreadyUploaded = uploadedMediaIds.current.get(photo.id);
          if (alreadyUploaded) {
            mediaIds.push(alreadyUploaded);
            continue;
          }

          if (!photo.file) continue;

          setPhotoState(photo.id, { status: 'uploading', progress: 0 });

          try {
            const result = await uploadImage(photo.file, {
              entityType: 'review',
              entityId,
              onProgress: (progress) => setPhotoState(photo.id, { status: 'uploading', progress }),
            });

            if (result.mediaId) {
              uploadedMediaIds.current.set(photo.id, result.mediaId);
              mediaIds.push(result.mediaId);
            }

            setPhotoState(photo.id, { status: 'uploaded', progress: 100 });
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Upload failed';
            setPhotoState(photo.id, { status: 'error', progress: 0, error: message });
            throw new Error(`Could not upload ${photo.file.name}: ${message}`);
          }
        }
      } finally {
        setIsUploading(false);
      }

      return mediaIds;
    },
    [entityId, setPhotoState, uploadImage]
  );

  return { uploadPhotos, uploadStates, isUploading, reset };
};
